import React, { useState, useEffect, useMemo } from "react";
import {
  ArrowBack,
  Assessment,
  CalendarToday,
  TrendingUp,
  Receipt,
  DateRange,
  Business,
  Storage,
} from "@material-ui/icons";
import { useHistory } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { Chart } from "react-google-charts";
import axios from "axios";
import Loader from "../../components/Loader";
import message from "../../helper/message";
import { formatAbbrValue } from "../../helper/formatValue";
import {
  Container,
  ModernHeader,
  HeaderLeft,
  HeaderContent,
  PageTitle,
  PageSubtitle,
  ResultsCard,
} from "../../components/common";

const E1_API_URL = process.env.REACT_APP_E1_API_PATH;
const E1_API_KEY = process.env.REACT_APP_E1_API_KEY;

const OBJECT_TYPES = [
  { value: "", label: "Todos" },
  { value: "540000006", label: "Purchase Quotation" },
  { value: "20", label: "Goods Receipt PO" },
  { value: "22", label: "Purchase Order" },
  { value: "1470000113", label: "Purchase Request" },
  { value: "17", label: "Sales Order" },
  { value: "23", label: "Sales Quotation" },
];

const OBJECT_TYPE_MAP = Object.fromEntries(
  OBJECT_TYPES.filter((o) => o.value).map((o) => [o.value, o.label]),
);

const MONTH_NAMES = [
  "Jan",
  "Fev",
  "Mar",
  "Abr",
  "Mai",
  "Jun",
  "Jul",
  "Ago",
  "Set",
  "Out",
  "Nov",
  "Dez",
];
const MONTH_FULL = [
  "Janeiro",
  "Fevereiro",
  "Março",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro",
];

function E1Dashboard() {
  const history = useHistory();
  const now = new Date();

  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState(null);
  const [companies, setCompanies] = useState([]);

  // Filtros
  const [companyId, setCompanyId] = useState("");
  const [baseCompanyId, setBaseCompanyId] = useState("");

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${E1_API_URL}/company/public`, {
          headers: { Authorization: token },
        });
        setCompanies(res.data || []);
      } catch (err) {
        console.error("Erro ao carregar empresas", err);
      }
    };
    fetchCompanies();
  }, []);
  const [objectType, setObjectType] = useState("");
  const [year, setYear] = useState(now.getFullYear());
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [typeChartMode, setTypeChartMode] = useState('count');
  const [companyChartMode, setCompanyChartMode] = useState('count');
  const [baseChartMode, setBaseChartMode] = useState('count');
  const [selectedMonth, setSelectedMonth] = useState("");

  useEffect(() => {
    setBaseCompanyId("");
  }, [companyId]);

  const companyDatabases = useMemo(() => {
    const comp = companies.find((c) => String(c.id) === String(companyId));
    return comp?.databases || [];
  }, [companies, companyId]);

  const baseOptions = useMemo(() => companyDatabases, [companyDatabases]);

  const companyLabelById = useMemo(() => {
    const map = {};
    companies.forEach((comp) => {
      if (comp?.id) map[comp.id] = comp.name || `Empresa ${comp.id}`;
      (comp?.databases || []).forEach((db) => {
        if (db.companyId) {
          map[db.companyId] =
            db.companyName || comp.name || `Empresa ${db.companyId}`;
        }
      });
    });
    return map;
  }, [companies]);

  const baseLabelByCompanyId = useMemo(() => {
    const map = {};
    companies.forEach((comp) => {
      (comp?.databases || []).forEach((db) => {
        if (db.companyId)
          map[db.companyId] = db.database || `Base ${db.companyId}`;
      });
    });
    return map;
  }, [companies]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const token = localStorage.getItem("token");
        const response = await axios.post(
          `${E1_API_URL}/document-statistics/summary`,
          {
            ...(companyId ? { groupId: parseInt(companyId) } : {}),
            ...(baseCompanyId ? { companyId: parseInt(baseCompanyId) } : {}),
            fromYear: parseInt(year),
            fromMonth: 1,
            toYear: parseInt(year),
            toMonth: 12,
            ...(objectType ? { objectType } : {}),
          },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: token,
            },
          },
        );
        setData(response.data);
      } catch (error) {
        console.error(error);
        message.error(
          error.response?.data?.message || "Erro ao buscar estatísticas",
        );
        setData(null);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [companyId, baseCompanyId, year, objectType]);

  const items = data?.items || [];

  const filteredItems = useMemo(() => {
    if (!selectedMonth) return items;
    const padded = String(selectedMonth).padStart(2, '0');
    return items.filter(item => item.month.endsWith('-' + padded));
  }, [items, selectedMonth]);

  const totals = useMemo(() => {
    if (!filteredItems.length) return { docCount: 0, totalValue: 0 };
    return {
      docCount: filteredItems.reduce((s, i) => s + (i.docCount || 0), 0),
      totalValue: filteredItems.reduce((s, i) => s + (i.totalValue || 0), 0),
    };
  }, [filteredItems]);

  const sortedItems = useMemo(() =>
    filteredItems.slice().sort(
      (a, b) =>
        a.month.localeCompare(b.month) ||
        String(a.companyId).localeCompare(String(b.companyId)),
    ),
  [filteredItems]);

  const totalPages = Math.max(1, Math.ceil(sortedItems.length / rowsPerPage));
  const paginatedItems = sortedItems.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  useEffect(() => { setCurrentPage(1); }, [data, rowsPerPage, selectedMonth]);

  const averagePerMonth =
    filteredItems.length > 0 ? Math.round(totals.docCount / filteredItems.length) : 0;
  const ticketMedio =
    totals.docCount > 0 ? totals.totalValue / totals.docCount : 0;

  const kpiVariation = useMemo(() => {
    const pct = (curr, prev) => (prev > 0 ? ((curr - prev) / prev) * 100 : null);
    if (!items.length) return { docs: null, value: null, ticket: null };
    const grouped = {};
    items.forEach((item) => {
      const m = item.month;
      if (!grouped[m]) grouped[m] = { docCount: 0, totalValue: 0 };
      grouped[m].docCount += item.docCount || 0;
      grouped[m].totalValue += item.totalValue || 0;
    });
    const months = Object.keys(grouped).sort();
    if (months.length < 2) return { docs: null, value: null, ticket: null };
    const last = grouped[months[months.length - 1]];
    const prev = grouped[months[months.length - 2]];
    const lastTicket = last.docCount > 0 ? last.totalValue / last.docCount : 0;
    const prevTicket = prev.docCount > 0 ? prev.totalValue / prev.docCount : 0;
    return {
      docs: pct(last.docCount, prev.docCount),
      value: pct(last.totalValue, prev.totalValue),
      ticket: pct(lastTicket, prevTicket),
    };
  }, [items]);

  // Chart data - Evolução mensal (agrupado por mês)
  const evolutionChartData = useMemo(() => {
    if (!filteredItems.length) return [["Mês", "Documentos", "Valor Total (R$)"]];

    const grouped = {};
    filteredItems.forEach((item) => {
      if (!grouped[item.month])
        grouped[item.month] = { docCount: 0, totalValue: 0 };
      grouped[item.month].docCount += item.docCount || 0;
      grouped[item.month].totalValue += item.totalValue || 0;
    });

    const chartData = [["Mês", "Documentos", "Valor Total (R$)"]];
    Object.keys(grouped)
      .sort()
      .forEach((month) => {
        const [y, m] = month.split("-");
        const monthIndex = parseInt(m) - 1;
        const label = `${MONTH_NAMES[monthIndex]}/${y.slice(2)}`;
        chartData.push([
          label,
          grouped[month].docCount,
          grouped[month].totalValue,
        ]);
      });
    return chartData;
  }, [filteredItems]);

  // Chart data - Documentos por mês (bar) - agrupado por mês
  const docsChartData = useMemo(() => {
    if (!filteredItems.length) return [["Mês", "Documentos"]];

    const grouped = {};
    filteredItems.forEach((item) => {
      if (!grouped[item.month]) grouped[item.month] = 0;
      grouped[item.month] += item.docCount || 0;
    });

    const chartData = [["Mês", "Documentos"]];
    Object.keys(grouped)
      .sort()
      .forEach((month) => {
        const [y, m] = month.split("-");
        const monthIndex = parseInt(m) - 1;
        const label = `${MONTH_NAMES[monthIndex]}/${y.slice(2)}`;
        chartData.push([label, grouped[month]]);
      });
    return chartData;
  }, [filteredItems]);

  // Chart data - Por tipo de documento (pie)
  const byObjectTypeData = useMemo(() => {
    if (!filteredItems.length) return [["Tipo", "Documentos"]];

    const grouped = {};
    filteredItems.forEach((item) => {
      const label =
        OBJECT_TYPE_MAP[item.objectType] || item.objectType || "Outros";
      if (!grouped[label]) grouped[label] = 0;
      grouped[label] += item.docCount || 0;
    });

    const chartData = [["Tipo", "Documentos"]];
    Object.entries(grouped).forEach(([label, count]) => {
      chartData.push([label, count]);
    });
    return chartData;
  }, [filteredItems]);

  // Chart data - Por tipo (valor)
  const byObjectTypeValueData = useMemo(() => {
    if (!filteredItems.length) return [["Tipo", "Valor (R$)"]];
    const grouped = {};
    filteredItems.forEach((item) => {
      const label = OBJECT_TYPE_MAP[item.objectType] || item.objectType || "Outros";
      if (!grouped[label]) grouped[label] = 0;
      grouped[label] += item.totalValue || 0;
    });
    const chartData = [["Tipo", "Valor (R$)"]];
    Object.entries(grouped).forEach(([label, value]) => chartData.push([label, value]));
    return chartData;
  }, [filteredItems]);

  // Chart data - Por empresa (pie - documentos)
  const byCompanyData = useMemo(() => {
    if (!filteredItems.length) return [["Empresa", "Documentos"]];

    const grouped = {};
    filteredItems.forEach((item) => {
      const label =
        companyLabelById[item.companyId] || `Empresa ${item.companyId}`;
      if (!grouped[label]) grouped[label] = 0;
      grouped[label] += item.docCount || 0;
    });

    const chartData = [["Empresa", "Documentos"]];
    Object.entries(grouped).forEach(([label, count]) => {
      chartData.push([label, count]);
    });
    return chartData;
  }, [filteredItems, companies]);

  // Chart data - Valor por empresa (bar)
  const valueByCompanyData = useMemo(() => {
    if (!filteredItems.length) return [["Empresa", "Valor (R$)"]];

    const grouped = {};
    filteredItems.forEach((item) => {
      const label =
        companyLabelById[item.companyId] || `Empresa ${item.companyId}`;
      if (!grouped[label]) grouped[label] = 0;
      grouped[label] += item.totalValue || 0;
    });

    const chartData = [["Empresa", "Valor (R$)"]];
    Object.entries(grouped)
      .sort((a, b) => b[1] - a[1])
      .forEach(([label, value]) => {
        chartData.push([label, value]);
      });
    return chartData;
  }, [filteredItems, companyLabelById]);

  // Chart data - Documentos por base
  const docsByBaseData = useMemo(() => {
    if (!filteredItems.length) return [["Base", "Documentos"]];

    const grouped = {};
    filteredItems.forEach((item) => {
      const label =
        baseLabelByCompanyId[item.companyId] || `Base ${item.companyId}`;
      if (!grouped[label]) grouped[label] = 0;
      grouped[label] += item.docCount || 0;
    });

    const chartData = [["Base", "Documentos"]];
    Object.entries(grouped).forEach(([label, count]) => {
      chartData.push([label, count]);
    });
    return chartData;
  }, [filteredItems, baseLabelByCompanyId]);

  // Chart data - Valor por base
  const valueByBaseData = useMemo(() => {
    if (!filteredItems.length) return [["Base", "Valor (R$)"]];

    const grouped = {};
    filteredItems.forEach((item) => {
      const label =
        baseLabelByCompanyId[item.companyId] || `Base ${item.companyId}`;
      if (!grouped[label]) grouped[label] = 0;
      grouped[label] += item.totalValue || 0;
    });

    const chartData = [["Base", "Valor (R$)"]];
    Object.entries(grouped)
      .sort((a, b) => b[1] - a[1])
      .forEach(([label, value]) => {
        chartData.push([label, value]);
      });
    return chartData;
  }, [filteredItems, baseLabelByCompanyId]);

  const evolutionChartOptions = {
    title: "Evolução Mensal",
    chartArea: { width: "75%", height: "70%" },
    hAxis: {
      title: "Mês",
      titleTextStyle: { color: "#2c3e50", fontSize: 14, bold: true },
    },
    seriesType: "line",
    series: {
      0: { color: "#3498db", targetAxisIndex: 0, lineWidth: 3, pointSize: 7 },
      1: { color: "#e67e22", targetAxisIndex: 1, lineWidth: 3, pointSize: 7 },
    },
    vAxes: {
      0: {
        title: "Documentos",
        titleTextStyle: { color: "#3498db" },
        gridlines: { color: "#f0f0f0" },
      },
      1: {
        title: "Valor Total (R$)",
        titleTextStyle: { color: "#e67e22" },
        format: "decimal",
        gridlines: { color: "#f5f5f5" },
      },
    },
    legend: { position: "top" },
    animation: { startup: true, duration: 1000, easing: "out" },
  };

  const docsChartOptions = {
    title: "Documentos por Mês",
    chartArea: { width: "80%", height: "70%" },
    hAxis: {
      title: "Mês",
      titleTextStyle: { color: "#2c3e50", fontSize: 14, bold: true },
    },
    vAxis: {
      title: "Quantidade",
      titleTextStyle: { color: "#2c3e50", fontSize: 14, bold: true },
      minValue: 0,
    },
    colors: ["#3498db"],
    legend: { position: "none" },
    animation: { startup: true, duration: 1000, easing: "out" },
  };

  const pieColors = [
    "#3498db",
    "#e67e22",
    "#27ae60",
    "#8e44ad",
    "#e74c3c",
    "#1abc9c",
    "#f39c12",
    "#2c3e50",
  ];

  const byObjectTypeOptions = {
    title: "Documentos por Tipo",
    chartArea: { width: "90%", height: "80%" },
    colors: pieColors,
    legend: { position: "right", textStyle: { fontSize: 13 } },
    pieHole: 0.4,
    animation: { startup: true, duration: 1000, easing: "out" },
  };

  const byCompanyOptions = {
    title: "Documentos por Empresa",
    chartArea: { width: "90%", height: "80%" },
    colors: pieColors,
    legend: { position: "right", textStyle: { fontSize: 13 } },
    pieHole: 0.4,
    animation: { startup: true, duration: 1000, easing: "out" },
  };

  const valueByCompanyOptions = {
    title: "Valor por Empresa",
    chartArea: { width: "70%", height: "70%" },
    hAxis: {
      title: "Valor (R$)",
      titleTextStyle: { color: "#2c3e50", fontSize: 14, bold: true },
      format: "short",
    },
    vAxis: { titleTextStyle: { color: "#2c3e50", fontSize: 14, bold: true } },
    colors: ["#e67e22"],
    legend: { position: "none" },
    animation: { startup: true, duration: 1000, easing: "out" },
  };

  const docsByBaseOptions = {
    title: "Documentos por Base",
    chartArea: { width: "90%", height: "80%" },
    colors: pieColors,
    legend: { position: "right", textStyle: { fontSize: 13 } },
    pieHole: 0.4,
    animation: { startup: true, duration: 1000, easing: "out" },
  };

  const valueByBaseOptions = {
    title: "Valor por Base",
    chartArea: { width: "70%", height: "70%" },
    hAxis: {
      title: "Valor (R$)",
      titleTextStyle: { color: "#2c3e50", fontSize: 14, bold: true },
      format: "short",
    },
    vAxis: { titleTextStyle: { color: "#2c3e50", fontSize: 14, bold: true } },
    colors: ["#1abc9c"],
    legend: { position: "none" },
    animation: { startup: true, duration: 1000, easing: "out" },
  };

  const exportCSV = () => {
    const BOM = "\uFEFF";
    const headers = "Mês;Empresa;Database;Tipo;Documentos;Valor Total;Ticket Médio;Atualização";
    const rows = sortedItems.map((item) => {
      const [y, m] = item.month.split("-");
      const mi = parseInt(m) - 1;
      const monthLabel = `${MONTH_FULL[mi]} ${y}`;
      const compName = companyLabelById[item.companyId] || item.companyId || "-";
      const dbName = baseLabelByCompanyId[item.companyId] || "-";
      const typeName = OBJECT_TYPE_MAP[item.objectType] || item.objectType || "-";
      const tm = item.docCount > 0 ? item.totalValue / item.docCount : 0;
      const updated = item.lastUpdatedAt
        ? new Date(item.lastUpdatedAt).toLocaleDateString("pt-BR")
        : "-";
      return [
        monthLabel, compName, dbName, typeName,
        (item.docCount || 0).toString(),
        (item.totalValue || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }),
        tm.toLocaleString("pt-BR", { style: "currency", currency: "BRL" }),
        updated,
      ].join(";");
    });
    const csv = BOM + headers + "\n" + rows.join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `e1-dashboard-${year}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const yearOptions = [];
  for (let y = now.getFullYear(); y >= 2020; y--) {
    yearOptions.push(y);
  }

  return (
    <Container>
      <ModernHeader>
        <HeaderLeft>
          <BackButton onClick={() => history.goBack()}>
            <ArrowBack />
          </BackButton>
          <HeaderContent>
            <PageTitle>
              <Assessment style={{ marginRight: "12px", fontSize: "32px" }} />
              Dashboard EssentialOne
            </PageTitle>
            <PageSubtitle>Estatísticas de documentos processados</PageSubtitle>
          </HeaderContent>
        </HeaderLeft>
      </ModernHeader>

      <ResultsCard>
        {/* Filtros */}
        <FiltersCard>
          <FiltersHeader>
            <DateRange style={{ fontSize: "20px", color: "#3498db" }} />
            <FiltersTitle>Filtros</FiltersTitle>
          </FiltersHeader>
          <FilterRow>
            <FilterGroup>
              <FilterLabel>
                <Business style={{ fontSize: "16px" }} />
                Empresa
              </FilterLabel>
              <FilterSelectWrapper>
                <FilterSelect
                  value={companyId}
                  onChange={(e) => setCompanyId(e.target.value)}
                >
                  <option value="">Todos</option>
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name || c.id}
                    </option>
                  ))}
                </FilterSelect>
              </FilterSelectWrapper>
            </FilterGroup>
            <FilterGroup>
              <FilterLabel>
                <Storage style={{ fontSize: "16px" }} />
                Base
              </FilterLabel>
              <FilterSelectWrapper>
                <FilterSelect
                  value={baseCompanyId}
                  onChange={(e) => setBaseCompanyId(e.target.value)}
                  disabled={!companyId || baseOptions.length === 0}
                >
                  <option value="">
                    {companyId ? "Todas" : "Selecione a empresa"}
                  </option>
                  {baseOptions.map((db) => (
                    <option key={db.companyId} value={db.companyId}>
                      {db.database}
                    </option>
                  ))}
                </FilterSelect>
              </FilterSelectWrapper>
            </FilterGroup>
            <FilterGroup>
              <FilterLabel>
                <Receipt style={{ fontSize: "16px" }} />
                Tipo Documento
              </FilterLabel>
              <FilterSelectWrapper>
                <FilterSelect
                  value={objectType}
                  onChange={(e) => setObjectType(e.target.value)}
                >
                  {OBJECT_TYPES.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </FilterSelect>
              </FilterSelectWrapper>
            </FilterGroup>
            <FilterGroup>
              <FilterLabel>
                <CalendarToday style={{ fontSize: "16px" }} />
                Ano
              </FilterLabel>
              <FilterSelectWrapper>
                <FilterSelect
                  value={year}
                  onChange={(e) => setYear(Number(e.target.value))}
                >
                  {yearOptions.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </FilterSelect>
              </FilterSelectWrapper>
            </FilterGroup>
            <FilterGroup>
              <FilterLabel>
                <CalendarToday style={{ fontSize: "16px" }} />
                Mês
              </FilterLabel>
              <FilterSelectWrapper>
                <FilterSelect
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                >
                  <option value="">Todos</option>
                  {MONTH_FULL.map((m, i) => {
                    if (parseInt(year) === now.getFullYear() && i + 1 > now.getMonth() + 1) return null;
                    return <option key={i + 1} value={i + 1}>{m}</option>;
                  })}
                </FilterSelect>
              </FilterSelectWrapper>
            </FilterGroup>
          </FilterRow>
        </FiltersCard>

        {isLoading && <Loader />}

        {!isLoading && data && (
          <>
            {/* Totalizadores */}
            <TotalizersGrid>
              <TotalizerCard color="#3498db">
                <TotalizerIcon color="#3498db">
                  <Receipt />
                </TotalizerIcon>
                <TotalizerContent>
                  <TotalizerValue>
                    {formatAbbrValue(totals.docCount)}
                  </TotalizerValue>
                  <TotalizerLabel>Total de Documentos</TotalizerLabel>
                  {!selectedMonth && kpiVariation.docs !== null && (
                    <VariationBadge positive={kpiVariation.docs >= 0}>
                      {kpiVariation.docs >= 0 ? "▲" : "▼"} {Math.abs(kpiVariation.docs).toFixed(1)}% vs ant.
                    </VariationBadge>
                  )}
                </TotalizerContent>
              </TotalizerCard>

              <TotalizerCard color="#e67e22">
                <TotalizerIcon color="#e67e22">
                  <TrendingUp />
                </TotalizerIcon>
                <TotalizerContent>
                  <TotalizerValue>
                    {formatAbbrValue(totals.totalValue, true)}
                  </TotalizerValue>
                  <TotalizerLabel>Valor Total</TotalizerLabel>
                  {!selectedMonth && kpiVariation.value !== null && (
                    <VariationBadge positive={kpiVariation.value >= 0}>
                      {kpiVariation.value >= 0 ? "▲" : "▼"} {Math.abs(kpiVariation.value).toFixed(1)}% vs ant.
                    </VariationBadge>
                  )}
                </TotalizerContent>
              </TotalizerCard>

              <TotalizerCard color="#27ae60">
                <TotalizerIcon color="#27ae60">
                  <CalendarToday />
                </TotalizerIcon>
                <TotalizerContent>
                  <TotalizerValue>
                    {formatAbbrValue(averagePerMonth)}
                  </TotalizerValue>
                  <TotalizerLabel>Média por Mês</TotalizerLabel>
                </TotalizerContent>
              </TotalizerCard>

              <TotalizerCard color="#8e44ad">
                <TotalizerIcon color="#8e44ad">
                  <Assessment />
                </TotalizerIcon>
                <TotalizerContent>
                  <TotalizerValue>
                    {formatAbbrValue(ticketMedio, true)}
                  </TotalizerValue>
                  <TotalizerLabel>Ticket Médio</TotalizerLabel>
                  {!selectedMonth && kpiVariation.ticket !== null && (
                    <VariationBadge positive={kpiVariation.ticket >= 0}>
                      {kpiVariation.ticket >= 0 ? "▲" : "▼"} {Math.abs(kpiVariation.ticket).toFixed(1)}% vs ant.
                    </VariationBadge>
                  )}
                </TotalizerContent>
              </TotalizerCard>
            </TotalizersGrid>

            {items.length > 0 ? (
              <>
                {/* Gráfico evolução - oculto quando mês selecionado */}
                {!selectedMonth && (
                  <ChartCard>
                    <Chart
                      chartType="ComboChart"
                      width="100%"
                      height="420px"
                      data={evolutionChartData}
                      options={evolutionChartOptions}
                    />
                  </ChartCard>
                )}

                {/* Gráfico barras docs - oculto quando mês selecionado */}
                {!selectedMonth && (
                  <ChartCard>
                    <Chart
                      chartType="ColumnChart"
                      width="100%"
                      height="380px"
                      data={docsChartData}
                      options={docsChartOptions}
                    />
                  </ChartCard>
                )}

                {/* Gráficos por tipo e por empresa com toggle */}
                <ChartsRow>
                  {(byObjectTypeData.length > 1 || byObjectTypeValueData.length > 1) && (
                    <ChartCard>
                      <ChartHeader>
                        <ChartTitle>Por Tipo</ChartTitle>
                        <ToggleGroup>
                          <ToggleBtn active={typeChartMode === 'count'} color="#3498db" onClick={() => setTypeChartMode('count')}>Documentos</ToggleBtn>
                          <ToggleBtn active={typeChartMode === 'value'} color="#3498db" onClick={() => setTypeChartMode('value')}>Valor</ToggleBtn>
                        </ToggleGroup>
                      </ChartHeader>
                      <Chart
                        chartType="PieChart"
                        width="100%"
                        height="340px"
                        data={typeChartMode === 'count' ? byObjectTypeData : byObjectTypeValueData}
                        options={{ ...byObjectTypeOptions, title: typeChartMode === 'count' ? "Documentos por Tipo" : "Valor por Tipo" }}
                      />
                    </ChartCard>
                  )}
                  {(byCompanyData.length > 1 || valueByCompanyData.length > 1) && (
                    <ChartCard>
                      <ChartHeader>
                        <ChartTitle>Por Empresa</ChartTitle>
                        <ToggleGroup>
                          <ToggleBtn active={companyChartMode === 'count'} color="#3498db" onClick={() => setCompanyChartMode('count')}>Documentos</ToggleBtn>
                          <ToggleBtn active={companyChartMode === 'value'} color="#3498db" onClick={() => setCompanyChartMode('value')}>Valor</ToggleBtn>
                        </ToggleGroup>
                      </ChartHeader>
                      {companyChartMode === 'count' ? (
                        <Chart chartType="PieChart" width="100%" height="340px" data={byCompanyData} options={byCompanyOptions} />
                      ) : (
                        <Chart chartType="BarChart" width="100%" height="340px" data={valueByCompanyData} options={valueByCompanyOptions} />
                      )}
                    </ChartCard>
                  )}
                </ChartsRow>

                {/* Gráfico por base com toggle */}
                {(docsByBaseData.length > 1 || valueByBaseData.length > 1) && (
                  <ChartCard>
                    <ChartHeader>
                      <ChartTitle>Por Base</ChartTitle>
                      <ToggleGroup>
                        <ToggleBtn active={baseChartMode === 'count'} color="#3498db" onClick={() => setBaseChartMode('count')}>Documentos</ToggleBtn>
                        <ToggleBtn active={baseChartMode === 'value'} color="#3498db" onClick={() => setBaseChartMode('value')}>Valor</ToggleBtn>
                      </ToggleGroup>
                    </ChartHeader>
                    {baseChartMode === 'count' ? (
                      <Chart chartType="PieChart" width="100%" height="360px" data={docsByBaseData} options={docsByBaseOptions} />
                    ) : (
                      <Chart chartType="BarChart" width="100%" height="360px" data={valueByBaseData} options={valueByBaseOptions} />
                    )}
                  </ChartCard>
                )}

                {/* Tabela detalhada */}
                <TableCard>
                  <TableTitleRow>
                    <TableTitle style={{ margin: 0 }}>Detalhamento</TableTitle>
                    <ExportBtn onClick={exportCSV}>⬇ Exportar CSV</ExportBtn>
                  </TableTitleRow>
                  <TableWrap>
                    <DataTable>
                      <thead>
                        <tr>
                          <TableHeader>Mês</TableHeader>
                          <TableHeader>Empresa</TableHeader>
                          <TableHeader>Database</TableHeader>
                          <TableHeader>Tipo</TableHeader>
                          <TableHeader align="right">Documentos</TableHeader>
                          <TableHeader align="right">Valor Total</TableHeader>
                          <TableHeader align="right">Ticket Médio</TableHeader>
                          <TableHeader align="right">Atualização</TableHeader>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedItems.map((item, idx) => {
                            const [y, m] = item.month.split("-");
                            const mi = parseInt(m) - 1;
                            const tm =
                              item.docCount > 0
                                ? item.totalValue / item.docCount
                                : 0;
                            const updated = item.lastUpdatedAt
                              ? new Date(item.lastUpdatedAt).toLocaleDateString(
                                  "pt-BR",
                                )
                              : "-";
                            const compName =
                              companyLabelById[item.companyId] ||
                              item.companyId ||
                              "-";
                            const dbName =
                              baseLabelByCompanyId[item.companyId] ||
                              "-";
                            const typeName =
                              OBJECT_TYPE_MAP[item.objectType] ||
                              item.objectType ||
                              "-";
                            return (
                              <TRow key={idx}>
                                <TCell>
                                  <strong>
                                    {MONTH_FULL[mi]} {y}
                                  </strong>
                                </TCell>
                                <TCell>{compName}</TCell>
                                <TCell>{dbName}</TCell>
                                <TCell>{typeName}</TCell>
                                <TCell align="right">
                                  <MetricBadge color="#3498db">
                                    {(item.docCount || 0).toLocaleString(
                                      "pt-BR",
                                    )}
                                  </MetricBadge>
                                </TCell>
                                <TCell align="right">
                                  <MetricBadge color="#e67e22">
                                    {(item.totalValue || 0).toLocaleString(
                                      "pt-BR",
                                      { style: "currency", currency: "BRL" },
                                    )}
                                  </MetricBadge>
                                </TCell>
                                <TCell align="right">
                                  <MetricBadge color="#27ae60">
                                    {tm.toLocaleString("pt-BR", {
                                      style: "currency",
                                      currency: "BRL",
                                    })}
                                  </MetricBadge>
                                </TCell>
                                <TCell
                                  align="right"
                                  style={{ color: "#6b7280", fontSize: "13px" }}
                                >
                                  {updated}
                                </TCell>
                              </TRow>
                            );
                          })}
                      </tbody>
                    </DataTable>
                  </TableWrap>
                  <PaginationContainer>
                    <PaginationInfo>
                      Mostrando {sortedItems.length === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1}–{Math.min(currentPage * rowsPerPage, sortedItems.length)} de {sortedItems.length} registros
                    </PaginationInfo>
                    <PaginationControls>
                      <PaginationLabel>Linhas por página:</PaginationLabel>
                      <PaginationSelect
                        value={rowsPerPage}
                        onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                      >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                      </PaginationSelect>
                      <PaginationButton disabled={currentPage === 1} onClick={() => setCurrentPage((p) => p - 1)}>Anterior</PaginationButton>
                      <PaginationPageInfo>Página {currentPage} de {totalPages}</PaginationPageInfo>
                      <PaginationButton disabled={currentPage >= totalPages} onClick={() => setCurrentPage((p) => p + 1)}>Próxima</PaginationButton>
                    </PaginationControls>
                  </PaginationContainer>
                </TableCard>
              </>
            ) : (
              <EmptyCard>
                <Assessment
                  style={{
                    fontSize: "64px",
                    color: "#cbd5e1",
                    marginBottom: "16px",
                  }}
                />
                <EmptyTitle>Nenhum dado encontrado</EmptyTitle>
                <EmptyText>
                  Não há documentos para o período e empresa selecionados.
                </EmptyText>
              </EmptyCard>
            )}
          </>
        )}

        {!isLoading && !data && (
          <EmptyCard>
            <Assessment
              style={{
                fontSize: "64px",
                color: "#cbd5e1",
                marginBottom: "16px",
              }}
            />
            <EmptyTitle>Selecione os filtros</EmptyTitle>
            <EmptyText>
              Preencha os filtros acima e clique em "Consultar" para visualizar
              as estatísticas.
            </EmptyText>
          </EmptyCard>
        )}
      </ResultsCard>
    </Container>
  );
}

export default E1Dashboard;

// ===== STYLED COMPONENTS =====

const fadeIn = keyframes`
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
`;

const BackButton = styled.button`
  background: rgba(52, 152, 219, 0.1);
  border: none;
  border-radius: 12px;
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #3498db;
  transition: all 0.3s ease;
  flex-shrink: 0;

  &:hover {
    background: rgba(52, 152, 219, 0.2);
    transform: translateX(-4px);
  }
`;

// Filters
const FiltersCard = styled.div`
  padding: 24px 0;
  margin-bottom: 24px;
  border-bottom: 1px solid #f0f0f0;
`;

const FiltersHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 20px;
`;

const FiltersTitle = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: #2c3e50;
  font-family: "Mulish", sans-serif;
`;

const FilterRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 160px 160px;
  gap: 20px;
  align-items: end;

  @media (max-width: 900px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FilterLabel = styled.label`
  font-size: 12px;
  font-weight: 700;
  color: #64748b;
  font-family: "Mulish", sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const FilterSelectWrapper = styled.div`
  position: relative;
  flex: 1;

  &::after {
    content: "";
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: 5px solid #9ca3af;
    pointer-events: none;
  }
`;

const FilterSelect = styled.select`
  width: 100%;
  padding: 11px 32px 11px 14px;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  font-size: 14px;
  font-family: "Mulish", sans-serif;
  background: #f9fafb;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #2c3e50;
  appearance: none;

  &:focus {
    outline: none;
    border-color: #3498db;
    box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.1);
    background: white;
  }

  &:hover {
    border-color: #d0d0d0;
    background: white;
  }
`;

const FilterInput = styled.input`
  width: 100%;
  padding: 11px 14px;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  font-size: 14px;
  font-family: "Mulish", sans-serif;
  background: #f9fafb;
  transition: all 0.2s ease;
  color: #2c3e50;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #3498db;
    box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.1);
    background: white;
  }

  &:hover {
    border-color: #d0d0d0;
    background: white;
  }

  &::placeholder {
    color: #9ca3af;
  }
`;


// Totalizers
const TotalizersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 20px;
  margin-bottom: 24px;
`;

const TotalizerCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border: 1px solid #e8e8e8;
  display: flex;
  align-items: center;
  gap: 16px;
  animation: ${fadeIn} 0.5s ease;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
  }
`;

const TotalizerIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 12px;
  background: ${(props) => `${props.color}15`};
  color: ${(props) => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    font-size: 28px;
  }
`;

const TotalizerContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const TotalizerValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #2c3e50;
  font-family: "Mulish", sans-serif;
`;

const TotalizerLabel = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
  font-family: "Mulish", sans-serif;
`;

const VariationBadge = styled.span`
  display: inline-block;
  margin-top: 6px;
  padding: 3px 9px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  font-family: "Mulish", sans-serif;
  background: ${(props) => (props.positive ? "#dcfce7" : "#fee2e2")};
  color: ${(props) => (props.positive ? "#15803d" : "#dc2626")};
`;

const ChartHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const ChartTitle = styled.h4`
  font-size: 15px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
  font-family: "Mulish", sans-serif;
`;

const ToggleGroup = styled.div`
  display: flex;
  gap: 4px;
`;

const ToggleBtn = styled.button`
  padding: 6px 14px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  border: 2px solid ${(props) => (props.active ? (props.color || '#3498db') : '#e5e7eb')};
  background: ${(props) => (props.active ? (props.color || '#3498db') : 'white')};
  color: ${(props) => (props.active ? 'white' : '#9ca3af')};
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: "Mulish", sans-serif;
`;

const TableTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const ExportBtn = styled.button`
  padding: 8px 16px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  border: none;
  background: linear-gradient(135deg, #27ae60 0%, #219a52 100%);
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: "Mulish", sans-serif;
  box-shadow: 0 2px 8px rgba(39, 174, 96, 0.3);

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(39, 174, 96, 0.4);
  }
`;

// Charts
const ChartsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 24px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }

  & > div {
    margin-bottom: 0;
  }
`;

const ChartCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border: 1px solid #e8e8e8;
  margin-bottom: 24px;
  animation: ${fadeIn} 0.5s ease;
`;

// Table
const TableCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border: 1px solid #e8e8e8;
  animation: ${fadeIn} 0.5s ease;
`;

const TableTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0 0 20px 0;
  font-family: "Mulish", sans-serif;
`;

const TableWrap = styled.div`
  overflow-x: auto;
`;

const DataTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.th`
  text-align: ${(props) => props.align || "left"};
  padding: 12px 16px;
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid #e5e7eb;
  font-family: "Mulish", sans-serif;
`;

const TRow = styled.tr`
  border-bottom: 1px solid #e5e7eb;
  transition: background 0.2s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f8f9fa;
  }
`;

const TCell = styled.td`
  text-align: ${(props) => props.align || "left"};
  padding: 16px;
  font-size: 14px;
  color: #2c3e50;
  font-family: "Mulish", sans-serif;
`;

const MetricBadge = styled.span`
  display: inline-block;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  background: ${(props) => `${props.color}15`};
  color: ${(props) => props.color};
`;

// Empty
const EmptyCard = styled.div`
  text-align: center;
  padding: 64px 24px;
  animation: ${fadeIn} 0.5s ease;
`;

const EmptyTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0 0 12px 0;
  font-family: "Mulish", sans-serif;
`;

const EmptyText = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0;
  font-family: "Mulish", sans-serif;
`;

// Pagination
const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0 0;
  margin-top: 16px;
  border-top: 1px solid #e5e7eb;
  flex-wrap: wrap;
  gap: 12px;
`;

const PaginationInfo = styled.span`
  font-size: 13px;
  color: #6b7280;
  font-family: "Mulish", sans-serif;
`;

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const PaginationLabel = styled.span`
  font-size: 13px;
  color: #6b7280;
  font-family: "Mulish", sans-serif;
`;

const PaginationSelect = styled.select`
  padding: 6px 10px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 13px;
  font-family: "Mulish", sans-serif;
  background: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #3498db;
    box-shadow: 0 0 0 2px rgba(52, 152, 219, 0.1);
  }
`;

const PaginationButton = styled.button`
  padding: 7px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 13px;
  font-family: "Mulish", sans-serif;
  background: white;
  color: #2c3e50;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #f3f4f6;
    border-color: #3498db;
    color: #3498db;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const PaginationPageInfo = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #2c3e50;
  font-family: "Mulish", sans-serif;
`;
